import axios from "axios";
import { useContext, createContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as Sentry from "@sentry/react";

export interface User {
  id: number,
  name: string,
  email: string,
  roles: string[],
  profile_photo?: {
    id: number,
    file_path: string
  },
  manager: boolean,
  chef: boolean,
}

export type LoginChallenge = {
  status: string,
  message: string,
  login_token: string,
  expires_at: string,
  expires_in: number
}

export type LoginSuccess = {
  status: string,
  token: string
}

export type LoginResponse = LoginChallenge | LoginSuccess

type AuthContextData = {
  token: string,
  user?: User,
  isLoading: boolean,
  fetchMeData: () => void,
  login: (username: string, password: string) => Promise<LoginResponse>,
  verifyLoginOtp: (loginToken: string, code: string) => Promise<void>,
  resendLoginOtp: (loginToken: string) => Promise<LoginChallenge>,
  forgetPassword: (username: string) => void,
  verifyForgetPasswordOtp: (code: string) => Promise<any>,
  resetPassword: (resetToken: string, password: string) => void,
  logout: () => void
}

const AuthContext = createContext<AuthContextData>({
  token: "",
  user: {
    id: 0,
    name: "",
    email: "",
    roles: [],
    manager: false,
    chef: false,
  },
  isLoading: false,
  fetchMeData: () => {},
  login: async () => ({ status: "", token: "" }),
  verifyLoginOtp: async () => { },
  resendLoginOtp: async () => ({ status: "", message: "", login_token: "", expires_at: "", expires_in: 0 }),
  forgetPassword: () => { },
  verifyForgetPasswordOtp: async () => { },
  resetPassword: () => { },
  logout: () => { }
});

const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [token, setToken] = useState<string>(localStorage.getItem("token") || "");
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(localStorage.getItem("token")));

  if(token){
    axios.defaults.headers.common = {
      Authorization: `Bearer ${token}`,
    }
  }

  const navigate = useNavigate();
  const [t] = useTranslation();
  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post("/login", {
        username, password
      });

      if (response?.status === 200 && response?.data?.token) {
        setToken(response.data.token);
        localStorage.setItem("token", response.data.token);
        navigate("/home");
        return response.data;
      }

      if (response?.status === 202 && response?.data?.login_token) {
        return response.data;
      }

      throw new Error(response?.data?.message);
    } catch (error: any) {
      throw new Error(error.response?.data?.message);
    }
  };

  const verifyLoginOtp = async (loginToken: string, code: string) => {
    try {
      const response = await axios.post("/login/verify-2fa", {
        login_token: loginToken,
        code
      });

      if (response?.status === 200 && response?.data?.token) {
        setToken(response.data.token);
        localStorage.setItem("token", response.data.token);
        navigate("/home");
        return;
      }

      throw new Error(response?.data?.message);
    } catch (error: any) {
      throw new Error(error.response?.data?.message);
    }
  };

  const resendLoginOtp = async (loginToken: string) => {
    try {
      const response = await axios.post("/login/resend-2fa", {
        login_token: loginToken
      });

      if (response?.status === 202 && response?.data?.login_token) {
        return response.data;
      }

      throw new Error(response?.data?.message);
    } catch (error: any) {
      throw new Error(error.response?.data?.message);
    }
  };
  const forgetPassword = async (username: string) => {
    try {
      const response = await axios.post("/forget-password", {
        username,
      });
      if (response?.data) {
        localStorage.setItem("forget-password-username", username)
        navigate("/reset-password");
        return;
      }
      throw new Error(response?.data?.message);
    } catch (error: any) {
      throw new Error(error.response?.data?.message);
    }
  };
  const verifyForgetPasswordOtp = (code: string) => {
    try {
      return axios.post("/forget-password/verify-otp", {
        username: localStorage.getItem("forget-password-username"),
        code
      });
    } catch (error: any) {
      throw new Error(error.response.data.message);
    }
  };
  const resetPassword = async (resetToken: string, password: string) => {
    try {
      const response = await axios.post("/reset-password", {
        "reset_token": resetToken,
        password
      });
      if (response?.data) {
        localStorage.removeItem("forget-password-username");
        Swal.fire({
          title: t('Success'),
          html: t('Password reset successfully.') + "<br>" + t("Now you can try to login usign your new password."),
          icon: "success"
        });
        navigate("/login");
        return;
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message);
    }
  };
  const fetchMeData = useCallback(() => {
    if (token) {
      setIsLoading(true);
      axios.get("/me").then((response: any) => {
        if (response?.name === 'AxiosError' || response?.status >= 400) {
          localStorage.removeItem("token");
          setToken("");
          setUser(undefined);
          Sentry.setUser(null);
          navigate("/");
        } else {
          setUser(response?.data);
          if (response?.data?.id || response?.data?.email) {
            Sentry.setUser({
              id: response.data.id ? String(response.data.id) : undefined,
              email: response.data.email,
              username: response.data.name,
            });
          }
        }
      }).catch(() => {
        localStorage.removeItem("token");
        setToken("");
        setUser(undefined);
        Sentry.setUser(null);
        navigate("/");
      }).finally(() => {
        setIsLoading(false);
      });
    } else {
      setUser(undefined);
      setIsLoading(false);
      Sentry.setUser(null);
    }
  }, [navigate, token]);
  useEffect(() => {
    fetchMeData();
  }, [fetchMeData]);

  const logout = () => {
    setUser(undefined);
    setToken("");
    setIsLoading(false);
    localStorage.removeItem("token");
    delete axios.defaults.headers.common.Authorization;
    Sentry.setUser(null);
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{
      token,
      user,
      isLoading,
      fetchMeData,
      login,
      verifyLoginOtp,
      resendLoginOtp,
      forgetPassword,
      verifyForgetPasswordOtp,
      resetPassword,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
